import { TILE, ROWS, W, H, MAX_HP, MAIN_LEVELS, MAX_LEVEL, effLevel, LEVELS, WEAPONS, WEAPON_ORDER, WEAPON_UNLOCK, DIFFICULTIES, ENEMIES, bossId } from "./config";
import { kitFor, bossPower, moveLabel, moveCooldown, weaponAngle, isWindup } from "./bossMoves";
import { fx, updateFx, drawFx, drawVignette, drawFade } from "./effects";
import { hazardFor, pickHazard, hazardInterval, drawHazard, burstColors, hazardColor } from "./hazards";
import { drawToast, drawHeart, drawEnemy, drawBoss, drawHero, drawWeapon, drawBossExtras } from "./sprites";
import { sfx, playMusic, songName } from "./audio";
import { biomeFor, drawBiome, drawDecor } from "./biomes";

let RNG = Math.random;
const mulberry = (a) => () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
const rnd = (a, b) => a + RNG() * (b - a);
const ri = (a, b) => Math.floor(rnd(a, b + 1));
const GRAV = 1800, JUMP = 780, RUN = 215;
const KEYMAP = { a: "left", arrowleft: "left", d: "right", arrowright: "right", w: "jump", arrowup: "jump", " ": "jump", j: "atk", k: "atk", x: "atk" };
const bossFor = (L) => {
  if (BOSSES[L]) return BOSSES[L];
  if (L <= MAIN_LEVELS) return null;
  const k = L - MAIN_LEVELS - 1;
  const base = BOSSES[[3, 6, 9, 12, 15][k % 5]];
  return { ...base, name: `${base.name} ${["II", "III", "IV"][Math.floor(k / 5)]}`, hp: Math.round(base.hp * (1.3 + k * 0.08)), speed: base.speed * 1.15, ring: base.ring ? base.ring + 2 : 6, aim: (base.aim || 0) + 1 };
};
export const POWERS = ["shield", "speed", "double", "magnet", "star", "boots"];
export const POWER_INFO = {
  shield: { label: "Shield", icon: "S", color: "#67E8F9", dur: 0 },
  speed: { label: "Speed Boots", icon: ">>", color: "#4ADE80", dur: 10 },
  double: { label: "Double Toast", icon: "x2", color: "#F59E0B", dur: 12 },
  magnet: { label: "Toast Magnet", icon: "U", color: "#F87171", dur: 12 },
  star: { label: "Invincible", icon: "*", color: "#FDE047", dur: 6 },
  boots: { label: "Double Jump Boots", icon: "^^", color: "#A78BFA", dur: 25 },
};
const BOSSES = {
  3: { taunt: "You dare enter MY crumb kingdom? I'll grind you to breadcrumbs!", name: "Crumb King", type: "crumb", scale: 3.2, hp: 22, speed: 90, jump: true, ring: 0, aim: 0 },
  6: { taunt: "Glorp... I will spread YOU on MY toast, little butter!", name: "Jam Kraken", type: "jam", scale: 3.2, hp: 40, speed: 70, jump: true, ring: 8, aim: 0 },
  9: { taunt: "Everything rots in the end. Even butter.", name: "Mold Titan", type: "mold", scale: 2.8, hp: 60, speed: 55, jump: false, ring: 10, aim: 1 },
  12: { taunt: "Welcome to my manor... you'll be toast by midnight!", name: "Burnt Baron", type: "ghost", scale: 3, hp: 65, speed: 70, fly: true, ring: 8, aim: 1 },
  15: { taunt: "I AM THE HEAT. NO SLICE ESCAPES MY SLOTS. BURN!", name: "Toaster Overlord", overlord: true, hp: 85, speed: 70, fly: true, ring: 9, aim: 2 },
};

export class Game {
  constructor(canvas, difficulty, cb, opts = {}) {
    this.c = canvas;
    this.ctx = canvas.getContext("2d");
    this.cb = cb;
    this.diffKey = difficulty;
    this.d = DIFFICULTIES[difficulty];
    this.maxHp = MAX_HP + (opts.extraHearts || 0) * 2;
    this.bonusPack = !!opts.bonusPack;
    this.skin = opts.skin || null;
    this.daily = opts.daily || null;
    this.run = { level: opts.startLevel || 1, lives: 3 + (opts.extraLives || 0), weapon: opts.weapon || "butter_knife", toastTotal: 0, kills: 0, score: 0, livesLost: 0, smashes: 0, stomps: 0, bees: 0, start: performance.now() };
    this.input = { left: false, right: false, up: false, down: false, jump: false, stickJump: false, atk: false };
    this.fired = new Set();
    this.paused = false;
    this.t = 0;
    this.hudKey = "";
    this.bind();
    this.startLevel();
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.loop);
  }

  bind() {
    this.onKey = (down) => (e) => {
      const k = KEYMAP[e.key.toLowerCase()];
      if (k) { if (k === "jump" && down && !e.repeat) this.queueJump(); this.input[k] = down; e.preventDefault(); }
      if (down && (e.key === "Escape" || e.key.toLowerCase() === "p")) this.cb.onPauseToggle?.();
    };
    this.kd = this.onKey(true);
    this.ku = this.onKey(false);
    this.pd = (e) => { if (e.pointerType === "mouse" && this.phase === "play" && !this.paused) this.tryAttack(); };
    this.bl = () => Object.keys(this.input).forEach((k) => { this.input[k] = false; });
    window.addEventListener("blur", this.bl);
    window.addEventListener("keydown", this.kd);
    window.addEventListener("keyup", this.ku);
    this.c.addEventListener("pointerdown", this.pd);
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    window.removeEventListener("blur", this.bl);
    window.removeEventListener("keydown", this.kd);
    window.removeEventListener("keyup", this.ku);
    this.c.removeEventListener("pointerdown", this.pd);
  }

  achieve(id) {
    if (this.fired.has(id)) return;
    this.fired.add(id);
    this.cb.onAchieve?.(id);
  }

  addScore(n) { this.run.score += Math.round(n * this.d.scoreMult); }

  loop = (now) => {
    const dt = Math.min(0.033, (now - this.last) / 1000);
    this.last = now;
    if (!this.paused) this.update(dt);
    this.draw();
    this.emitHud();
    this.raf = requestAnimationFrame(this.loop);
  };

  emitHud() {
    const b = this.boss, pw = this.pw;
    const powers = POWERS.filter((k) => (k === "shield" ? pw.shield : pw[k] > 0)).map((k) => (k === "shield" ? k : `${k}:${Math.ceil(pw[k])}`));
    const hud = { powers, maxHp: this.maxHp, hp: Math.max(0, this.p.hp), lives: this.run.lives, toast: this.levelToast, goal: this.goal, level: this.run.level, weapon: this.run.weapon, score: this.run.score, boss: b && !this.bossDead ? Math.ceil((b.hp / b.maxHp) * 100) : null, bossName: b?.name, exitOpen: this.exitOpen, timeLeft: Math.max(0, Math.ceil(this.timeLimit - this.levelTime)) };
    const key = JSON.stringify(hud);
    if (key !== this.hudKey) { this.hudKey = key; this.cb.onHud?.(hud); }
  }

  stats() {
    return { score: this.run.score, level: this.run.level, toast: this.run.toastTotal, kills: this.run.kills, livesLost: this.run.livesLost, difficulty: this.diffKey, time: Math.round((performance.now() - this.run.start) / 1000) };
  }

  // ---------- level ----------
  startLevel() {
    const L = this.run.level;
    this.theme = LEVELS[L - 1];
    this.goal = Math.max(3, Math.round(this.theme.goal * this.d.goal));
    this.levelToast = 0; this.levelDamage = false; this.levelTime = 0;
    this.bossCfg = bossFor(L);
    this.pal = biomeFor(L);
    this.hazard = hazardFor(this.pal.kind);
    this.hazards = []; this.hazardT = 6; this.hazardsSpawned = 0; this.hazardHit = false; this.bossHurt = false;
    this.skyGrad = null;
    this.timedOut = false;
    this.timeLimit = this.diffKey === "hard" ? 135 : 90;
    this.boss = null; this.bossDead = !this.bossCfg; this.bossActive = false;
    this.enemies = []; this.bullets = []; this.toasts = []; this.pickups = []; this.parts = []; this.texts = []; this.fx = [];
    this.genLevel(L);
    this.p = { x: 3 * TILE, y: 16 * TILE - 12, w: 11, h: 11, r: 11, vx: 0, vy: 0, onGround: false, dir: 1, hp: this.maxHp, inv: 2, atk: 0, swing: 0, kbT: 0, walk: 0, jumpHeld: false };
    this.safe = { x: this.p.x, y: this.p.y };
    this.pw = this.bonusPack ? { shield: true, speed: 0, double: 25, magnet: 25, star: 6, boots: 0 } : { shield: false, speed: 0, double: 0, magnet: 0, star: 0, boots: 0 };
    this.powerT = 35; this.shake = 0; this.cam = 0;
    const wu = Object.entries(WEAPON_UNLOCK).filter(([lv]) => +lv <= L).map(([, w]) => w).pop();
    if (wu && WEAPON_ORDER.indexOf(wu) > WEAPON_ORDER.indexOf(this.run.weapon)) {
      const s = this.spots[Math.floor(this.spots.length * 0.3)];
      this.pickups.push({ type: "weapon", weapon: wu, x: s[0], y: s[1] - 10 });
    }
    if (L >= 5) this.achieve("level_5");
    if (L >= 8) this.achieve("globetrotter");
    if (this.skin) this.achieve("dressed_up");
    if (L >= MAIN_LEVELS) this.achieve("final_level");
    this.phase = "intro"; this.phaseT = 2.4;
    sfx("level");
    playMusic("level", this.run.level);
  }

  genLevel(L) {
    if (this.daily) RNG = mulberry(this.daily.seed);
    this.genLevelInner(L);
    RNG = Math.random;
  }

  genLevelInner(L) {
    const arena = this.bossCfg ? 32 : 0;
    const SL = effLevel(L);
    const cols = 135 + Math.min(L, MAIN_LEVELS) * 11 + arena;
    const g = Array.from({ length: ROWS }, () => new Array(cols).fill(0));
    const heights = new Array(cols).fill(ROWS);
    const fill = (x, top) => { heights[x] = top; for (let y = top; y < ROWS; y++) g[y][x] = 1; };
    const maxPit = this.diffKey === "easy" ? 2 : SL < 4 ? 2 : SL < 8 ? 3 : 4;
    let x = 0, top = 16;
    const groundSpots = [], platSpots = [];
    while (x < 10) fill(x++, top);
    const endPlay = cols - arena - 6;
    while (x < endPlay) {
      if (RNG() < 0.28 + L * 0.02 && x > 12) {
        x += ri(2, maxPit);
      }
      top = Math.max(12, Math.min(17, top + ri(-2, 2)));
      const len = ri(4, 10 - Math.floor(L / 3));
      for (let i = 0; i < len && x < endPlay; i++, x++) { fill(x, top); if (i > 0 && i < len - 1) groundSpots.push([x, top]); }
      if (RNG() < 0.55) {
        const pw = ri(3, 5), py = top - ri(3, 4), px = x - len + ri(0, 2);
        for (let i = 0; i < pw; i++) if (g[py] && px + i < cols) { g[py][px + i] = 2; platSpots.push([px + i, py]); }
      }
    }
    while (x < cols) fill(x++, 16);
    for (let y = 0; y < ROWS; y++) { g[y][0] = 1; g[y][cols - 1] = 1; }
    this.grid = g; this.cols = cols; this.levelW = cols * TILE;
    this.arenaX = this.bossCfg ? (cols - arena) * TILE : null;
    this.exit = { x: (cols - 4) * TILE + 16, y: 16 * TILE - 22 };
    this.exitOpen = false;
    const toSpot = ([tx, ty]) => [tx * TILE + 16, ty * TILE - 20];
    this.spots = [...groundSpots, ...platSpots].map(toSpot).sort((a, b) => a[0] - b[0]);
    const highs = groundSpots.filter(([gx, gy]) => gx > 15 && gx < endPlay - 8 && gy >= 14 && !g[gy - 7]?.[gx] && !g[gy - 4]?.[gx]);
    for (let n = 0; n < 2 && highs.length; n++) {
      const [hx, hy] = highs.splice(ri(0, highs.length - 1), 1)[0];
      const py = hy - 7;
      for (let i = 0; i < 4; i++) if (hx + i < endPlay) g[py][hx + i] = 2;
      for (let i = 0; i < 3; i++) this.toasts.push({ x: (hx + i) * TILE + 32, y: py * TILE - 24, seed: i, secret: true });
    }
    if (RNG() < 0.6 && platSpots.length) { const [bx, by] = platSpots[ri(0, platSpots.length - 1)]; this.pickups.push({ type: "power", kind: "boots", x: bx * TILE + 16, y: by * TILE - 22 }); }
    const tSpots = [...platSpots.map(toSpot), ...groundSpots.map(toSpot)].filter(([sx]) => sx > 8 * TILE);
    const need = this.goal + 6;
    for (let i = 0; i < need && tSpots.length; i++) {
      const [sx, sy] = tSpots.splice(ri(0, tSpots.length - 1), 1)[0];
      this.toasts.push({ x: sx, y: sy - 8, seed: RNG() * 6 });
    }
    const count = Math.max(3, Math.round((4 + SL * 2.2) * 1.35 * this.d.enemyCount));
    const eSpots = groundSpots.filter(([gx]) => gx > 14);
    const pool = Object.entries(ENEMIES).filter(([, e]) => e.minLevel <= L);
    for (let i = 0; i < count && eSpots.length; i++) {
      const [gx, gy] = eSpots.splice(ri(0, eSpots.length - 1), 1)[0];
      let roll = RNG() * pool.reduce((s, [, e]) => s + e.weight, 0);
      let pick = pool[0];
      for (const p of pool) { roll -= p[1].weight; if (roll <= 0) { pick = p; break; } }
      this.addEnemy(pick[0], pick[1], gx * TILE + 16, gy * TILE - pick[1].r - 2 - (pick[0] === "bee" ? 80 : 0), L);
    }
    this.renderBg();
  }

  addEnemy(type, base, x, y, L) {
    const EL = effLevel(L);
    const hp = Math.max(1, Math.ceil(base.hp * (1 + (EL - 1) * 0.12) * this.d.enemyHp));
    const speed = base.speed * (1 + (EL - 1) * 0.06) * this.d.enemySpeed;
    this.enemies.push({ type, x, y, r: base.r, w: base.r, h: base.r, hp, speed, vx: (RNG() < 0.5 ? -1 : 1) * speed, vy: 0, pts: base.pts, hitT: 0, kbT: 0, fireT: rnd(1.5, 3), jumpT: rnd(1, 2), seed: RNG() * 10 });
  }

  renderBg() {
    const bg = document.createElement("canvas");
    bg.width = this.levelW; bg.height = H;
    const c = bg.getContext("2d");
    const g = this.grid, T = TILE;
    const isG = (x, y) => x >= 0 && x < this.cols && y >= 0 && y < ROWS && g[y][x] === 1;
    for (let x = 1; x < this.cols - 1; x++) {
      let top = -1;
      for (let y = 0; y < ROWS; y++) if (g[y][x] === 1) { top = y; break; }
      if (top < 0) continue;
      const px = x * T, py = top * T;
      const grad = c.createLinearGradient(0, py, 0, H);
      grad.addColorStop(0, this.pal.crumb); grad.addColorStop(1, this.pal.crumbHole);
      c.fillStyle = grad; c.fillRect(px, py, T, H - py);
      c.fillStyle = this.pal.crumbHole;
      for (let k = 0; k < 3; k++) { const hx = px + ((x * 37 + k * 13) % 26) + 3, hy = py + 22 + ((x * 53 + k * 41) % Math.max(10, H - py - 30)); c.beginPath(); c.ellipse(hx, hy, 3 + (k % 2) * 2, 2 + (k % 2), 0, 0, Math.PI * 2); c.fill(); }
      if (!isG(x - 1, top) ) { c.fillStyle = this.pal.crustDark; c.fillRect(px, py, 6, H - py); }
      if (!isG(x + 1, top)) { c.fillStyle = this.pal.crustDark; c.fillRect(px + T - 6, py, 6, H - py); }
      c.fillStyle = this.pal.crust; c.fillRect(px, py, T, 12);
      c.fillStyle = this.pal.crustHi; c.fillRect(px, py + 2, T, 3);
      c.fillStyle = "rgba(74,37,17,0.25)"; c.fillRect(px, py + 12, T, 4);
      if (this.pal.tuft) { c.fillStyle = this.pal.tuft; for (let k = 0; k < 3; k++) { const tx = px + 4 + ((x * 7 + k * 11) % 24); c.beginPath(); c.moveTo(tx - 3, py + 1); c.lineTo(tx, py - 6 - (k % 2) * 3); c.lineTo(tx + 3, py + 1); c.fill(); } }
      if (!isG(x - 1, top)) { c.fillStyle = this.pal.crust; c.beginPath(); c.arc(px + 6, py + 6, 6, 0, Math.PI * 2); c.fill(); }
      if (!isG(x + 1, top)) { c.fillStyle = this.pal.crust; c.beginPath(); c.arc(px + T - 6, py + 6, 6, 0, Math.PI * 2); c.fill(); }
      for (let y = top + 1; y < ROWS; y++) if (g[y][x] === 1 && g[y - 1][x] !== 1) { c.fillStyle = this.pal.crust; c.fillRect(px, y * T, T, 10); }
      c.fillStyle = "rgba(0,0,0,0.09)"; c.fillRect(px, py + 46, T, 7); c.fillRect(px, py + 108, T, 5);
      c.fillStyle = "rgba(255,255,255,0.1)"; c.fillRect(px, py + 44, T, 2);
      const hs = ((x * 9301 + 49297) % 233280) / 233280;
      if (hs < 0.35 && x > 3 && x < this.cols - 6 && isG(x - 1, top) && isG(x + 1, top) && g[top - 1]?.[x] === 0) drawDecor(c, this.pal.kind, px + 8 + hs * 40, py + 1, (hs * 7.3) % 1);
    }
    for (let y = 0; y < ROWS; y++) for (let x = 0; x < this.cols; x++) {
      if (g[y][x] !== 2 || (x > 0 && g[y][x - 1] === 2)) continue;
      let w = 0; while (g[y][x + w] === 2) w++;
      const px = x * T + 2, py = y * T, pw = w * T - 4;
      c.fillStyle = "rgba(74,37,17,0.2)"; c.beginPath(); c.roundRect(px + 4, py + 10, pw, 14, 7); c.fill();
      c.fillStyle = this.pal.crustDark; c.beginPath(); c.roundRect(px, py + 4, pw, 14, 7); c.fill();
      c.fillStyle = this.pal.crust; c.beginPath(); c.roundRect(px, py, pw, 14, 7); c.fill();
      c.fillStyle = this.pal.platTop; c.beginPath(); c.roundRect(px + 4, py + 3, pw - 8, 7, 4); c.fill();
      c.fillStyle = "#FFE45C"; c.beginPath(); c.roundRect(px + pw * 0.3, py + 3, pw * 0.25, 5, 3); c.fill();
    }
    c.fillStyle = this.pal.crustDark; c.fillRect(0, 0, 8, H); c.fillRect(this.levelW - 8, 0, 8, H);
    this.bg = bg;
  }

  solid(tx, ty, oneWayOk = false) {
    if (tx < 0 || tx >= this.cols) return true;
    if (ty < 0 || ty >= ROWS) return false;
    const v = this.grid[ty][tx];
    return v === 1 || (v === 2 && oneWayOk);
  }

  physics(b, dt, gravity = true) {
    if (gravity) b.vy = Math.min(b.vy + GRAV * dt, 950);
    b.x += b.vx * dt;
    b.hitWall = false;
    if (this.boxHits(b, false)) {
      b.x = b.vx > 0 ? Math.floor((b.x + b.w) / TILE) * TILE - b.w - 0.01 : (Math.floor((b.x - b.w) / TILE) + 1) * TILE + b.w + 0.01;
      b.hitWall = true;
    }
    const prevBottom = b.y + b.h;
    b.y += b.vy * dt;
    b.onGround = false;
    if (b.vy > 0) {
      const ty = Math.floor((b.y + b.h) / TILE);
      const landOneWay = prevBottom <= ty * TILE + 1;
      if (this.rowHits(b, ty, landOneWay)) { b.y = ty * TILE - b.h - 0.01; b.vy = 0; b.onGround = true; }
    } else if (b.vy < 0) {
      const ty = Math.floor((b.y - b.h) / TILE);
      if (this.rowHits(b, ty, false)) { b.y = (ty + 1) * TILE + b.h + 0.01; b.vy = 0; }
    }
  }

  rowHits(b, ty, oneWay) {
    for (let tx = Math.floor((b.x - b.w + 1) / TILE); tx <= Math.floor((b.x + b.w - 1) / TILE); tx++) if (this.solid(tx, ty, oneWay)) return true;
    return false;
  }

  boxHits(b) {
    for (let ty = Math.floor((b.y - b.h + 1) / TILE); ty <= Math.floor((b.y + b.h - 1) / TILE); ty++) if (this.rowHits(b, ty, false)) return true;
    return false;
  }

  burst(x, y, color, n = 10, sp = 120) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, s = rnd(sp * 0.3, sp);
      this.parts.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: rnd(0.3, 0.7), color, size: ri(2, 4) });
    }
  }

  text(x, y, txt, color = "#FEF08A") { this.texts.push({ x, y, txt, color, life: 0.9 }); }

  // ---------- combat ----------
  queueJump() { if (this.p) this.p.jumpBuf = 0.18; }

  tryAttack() {
    const p = this.p;
    if (p.atk > 0) return;
    const w = WEAPONS[this.run.weapon];
    p.atk = w.cd; p.swing = 0.16;
    sfx("swing");
    const targets = this.boss && !this.bossDead ? [...this.enemies, this.boss] : this.enemies;
    targets.forEach((e) => {
      const dx = e.x - p.x, dy = e.y - p.y;
      const front = Math.sign(dx) === p.dir || Math.abs(dx) < e.r + 10;
      if (!(front && Math.abs(dx) < w.range + e.r + 4 && Math.abs(dy) < w.range * 0.6 + e.r)) return;
      this.damageEnemy(e, w.dmg);
    });
    this.hazards.forEach((h) => {
      if (h.type === "burst" || h.t < h.warn || Math.abs(h.x - p.x) > w.range + h.r || Math.abs(h.y - p.y) > w.range * 0.7 + h.r) return;
      if (Math.sign(h.x - p.x) !== p.dir && Math.abs(h.x - p.x) > h.r + 10) return;
      h.dead = true; this.popHazard(h); this.addScore(20); this.text(h.x, h.y - 16, "SMASH!", "#FDE047");
      if (++this.run.smashes >= 10) this.achieve("smash_10");
    });
  }

  damageEnemy(e, dmg) {
    e.hp -= dmg; e.hitT = 0.12;
    this.text(e.x, e.y - e.r - 4, `-${dmg}`, "#FCA5A5");
    if (!e.isBoss && e.type !== "turret") { e.kbT = 0.15; e.vx = this.p.dir * 260; e.vy = -220; }
    this.burst(e.x, e.y, "#FDE68A", 5, 90);
    fx.spark(this.fx, e.x + rnd(-6, 6), e.y + rnd(-6, 6));
    sfx("hit");
    if (e.hp > 0) return;
    if (e.isBoss) {
      this.bossDead = true;
      this.burst(e.x, e.y, "#F97316", 60, 260);
      this.burst(e.x, e.y, "#FDE047", 40, 200);
      this.shake = 0.6;
      this.addScore(1500 * Math.ceil(this.run.level / 3));
      this.achieve("boss_slayer");
      if (!this.bossHurt) this.achieve("boss_flawless");
      this.cb.onDefeat?.(bossId(e.name));
      if (this.bossCfg.overlord) this.achieve("overlord");
      for (let i = 0; i < 6; i++) this.toasts.push({ x: e.x + rnd(-60, 60), y: e.y - rnd(0, 40), seed: i });
      this.text(e.x, e.y - 60, `${e.name.toUpperCase()} DEFEATED!`, "#FDE047");
      sfx("victory");
      playMusic("level", this.run.level);
      return;
    }
    this.enemies = this.enemies.filter((x) => x !== e);
    this.run.kills++;
    this.cb.onDefeat?.(e.type);
    this.addScore(e.pts);
    this.burst(e.x, e.y, e.type === "mold" ? "#10B981" : e.type === "jam" ? "#8B5CF6" : "#B45309", 14);
    sfx("kill");
    fx.ring(this.fx, e.x, e.y, "#FFF3B0", e.r * 3);
    this.achieve("first_blood");
    if (this.run.kills >= 50) this.achieve("crumb_crusher");
    if (this.run.kills >= 100) this.achieve("crumb_apocalypse");
    if (e.type === "bee" && ++this.run.bees >= 10) this.achieve("bee_gone");
    if (Math.random() < this.d.heartDrop) this.pickups.push({ type: "heart", x: e.x, y: e.y - 10 });
    else if (Math.random() < (this.diffKey === "easy" ? 0.06 : 0.03)) this.spawnPower(e.x, e.y - 10);
  }

  spawnPower(x, y) {
    if (x === undefined) {
      const ahead = this.spots.filter(([sx]) => sx > this.p.x + 200 && sx < this.p.x + 700);
      if (!ahead.length) return;
      [x, y] = ahead[ri(0, ahead.length - 1)];
    }
    this.pickups.push({ type: "power", kind: Math.random() < 0.12 ? "boots" : POWERS[ri(0, POWERS.length - 2)], x, y });
  }

  applyPower(kind) {
    if (kind === "shield") this.pw.shield = true;
    else this.pw[kind] = POWER_INFO[kind].dur;
    this.text(this.p.x, this.p.y - 24, POWER_INFO[kind].label + "!", POWER_INFO[kind].color);
    if (POWERS.filter((k) => (k === "shield" ? this.pw.shield : this.pw[k] > 0)).length >= 3) this.achieve("power_stack");
    sfx("weapon");
  }

  hurtPlayer(sx, force = false) {
    const p = this.p;
    if (this.phase !== "play") return;
    if (!force && (p.inv > 0 || this.pw.star > 0)) return;
    if (!force && this.pw.shield) {
      this.pw.shield = false; p.inv = 1;
      this.text(p.x, p.y - 24, "SHIELD BLOCK!", "#67E8F9");
      sfx("hit");
      return;
    }
    p.hp -= this.d.hitDmg;
    if (this.boss && !this.bossDead) this.bossHurt = true;
    p.inv = this.d.iframes;
    this.levelDamage = true;
    p.kbT = 0.18; p.vx = (p.x < sx ? -1 : 1) * 280; p.vy = -320;
    this.shake = 0.3;
    this.burst(p.x, p.y, "#FDE047", 12);
    fx.spark(this.fx, p.x, p.y, "#FCA5A5");
    sfx("hurt");
    if (p.hp > 0) return;
    this.run.lives--;
    this.run.livesLost++;
    sfx("die");
    if (this.run.lives <= 0) { this.phase = "gameover"; this.phaseT = 2.2; this.achieve("burnt_toast"); }
    else { this.phase = "dying"; this.phaseT = 2; }
  }

  timeUp() {
    this.timedOut = true;
    this.p.hp = 0;
    this.run.lives--; this.run.livesLost++;
    sfx("die");
    if (this.run.lives <= 0) { this.phase = "gameover"; this.phaseT = 2.2; this.achieve("burnt_toast"); }
    else { this.phase = "dying"; this.phaseT = 2; }
  }

  levelComplete() {
    const L = this.run.level;
    this.addScore(500 * L);
    if (!this.levelDamage) this.achieve("untouchable");
    if (this.levelTime < 30) this.achieve("speedy");
    if (this.timeLimit - this.levelTime < 10) this.achieve("clutch");
    if (this.hazardsSpawned >= 3 && !this.hazardHit) this.achieve("hazard_dodger");
    if (this.run.lives === 1) this.achieve("last_life");
    sfx("clear");
    if (this.daily) { this.phase = "dailydone"; this.phaseT = 2; return; }
    if (L === MAX_LEVEL) { this.achieve("beat_bonus"); this.phase = "victory"; this.phaseT = 2.5; return; }
    if (L === MAIN_LEVELS) {
      this.achieve(`beat_${this.diffKey}`);
      if (this.run.livesLost === 0) this.achieve("flawless");
      this.phase = "victory"; this.phaseT = 2.5;
    } else { this.phase = "exiting"; this.phaseT = 1.6; }
  }

  // ---------- update ----------
  update(dt) {
    this.t += dt;
    this.shake = Math.max(0, this.shake - dt);
    this.parts.forEach((q) => { q.x += q.vx * dt; q.y += q.vy * dt; q.vy += 300 * dt; q.life -= dt; });
    this.parts = this.parts.filter((q) => q.life > 0);
    this.texts.forEach((q) => { q.y -= 30 * dt; q.life -= dt; });
    this.texts = this.texts.filter((q) => q.life > 0);
    this.fx = updateFx(this.fx, dt);
    if (this.phase !== "play") {
      this.phaseT -= dt;
      if (this.phaseT > 0) return;
      if (this.phase === "intro" || this.phase === "bossintro") this.phase = "play";
      else if (this.phase === "dying") this.startLevel();
      else if (this.phase === "exiting") { this.run.level++; this.startLevel(); }
      else if (this.phase === "gameover") { this.phase = "done"; this.cb.onGameOver?.(this.stats()); }
      else if (this.phase === "victory") { this.phase = "done"; this.cb.onVictory?.(this.stats()); }
      else if (this.phase === "dailydone") { this.phase = "done"; this.cb.onDailyComplete?.(this.stats()); }
      return;
    }
    this.levelTime += dt;
    if (this.levelTime >= this.timeLimit) { this.timeUp(); return; }
    this.updatePlayer(dt);
    this.updateEnemies(dt);
    if (this.bossCfg && !this.bossDead) this.updateBoss(dt);
    this.updateBullets(dt);
    if (this.hazard || this.hazards.length) this.updateHazards(dt);
    this.updateItems(dt);
    const minCam = this.bossActive && !this.bossDead ? this.arenaX : 0;
    const target = Math.max(minCam, Math.min(this.levelW - W, this.p.x - W * 0.4));
    this.cam += (target - this.cam) * Math.min(1, dt * 8);
  }

  updatePlayer(dt) {
    const p = this.p, i = this.input;
    const dirIn = (i.right ? 1 : 0) - (i.left ? 1 : 0);
    const spd = this.pw.speed > 0 ? RUN * 1.45 : RUN;
    if (p.kbT > 0) p.kbT -= dt;
    else p.vx = dirIn * spd;
    if (dirIn) { p.dir = dirIn; p.walk += dt; }
    const jumpNow = i.jump || i.stickJump;
    if (jumpNow && !p.jumpHeld) this.queueJump();
    p.jumpHeld = jumpNow;
    p.jumpBuf = (p.jumpBuf || 0) - dt;
    p.coyote = p.onGround ? 0.12 : (p.coyote || 0) - dt;
    if (p.onGround) p.airJump = false;
    if (p.jumpBuf > 0 && p.coyote > 0) { p.vy = -JUMP; p.jumpBuf = 0; p.coyote = 0; sfx("swing"); fx.dust(this.fx, p.x, p.y + p.h, 4); }
    else if (p.jumpBuf > 0 && this.pw.boots > 0 && !p.airJump && !p.onGround) {
      p.vy = -JUMP * 0.92; p.jumpBuf = 0; p.airJump = true;
      this.burst(p.x, p.y + 12, "#A78BFA", 12, 140); sfx("toast"); this.achieve("double_jump");
    }
    const wasAir = !p.onGround, vy0 = p.vy;
    this.physics(p, dt);
    if (wasAir && p.onGround && vy0 > 380) fx.dust(this.fx, p.x, p.y + p.h, 6);
    if (this.bossActive && !this.bossDead) p.x = Math.max(this.arenaX + p.w, p.x);
    if (p.onGround) this.safe = { x: p.x, y: p.y };
    if (p.y > H + 60) {
      this.text(p.x, H - 60, "FELL!", "#EF4444");
      this.hurtPlayer(p.x, true);
      if (this.phase === "play") { p.x = this.safe.x - p.dir * 40; p.y = this.safe.y - 40; p.vx = 0; p.vy = 0; p.kbT = 0; }
    }
    p.atk -= dt; p.swing -= dt; p.inv -= dt;
    ["speed", "double", "magnet", "star", "boots"].forEach((k) => { this.pw[k] = Math.max(0, this.pw[k] - dt); });
    this.powerT -= dt;
    if (this.powerT <= 0) { this.spawnPower(); this.powerT = this.diffKey === "easy" ? 30 : 45; }
    if (i.atk) this.tryAttack();
    if (this.bossCfg && !this.bossActive && p.x > this.arenaX + 4 * TILE) this.spawnBoss();
  }

  updateEnemies(dt) {
    const p = this.p, L = this.run.level;
    for (const e of this.enemies) {
      if (Math.abs(e.x - p.x) > W) continue;
      e.hitT -= dt;
      if (e.type === "bee") {
        e.baseY = e.baseY ?? e.y; e.homeX = e.homeX ?? e.x;
        const near = Math.abs(p.x - e.x) < 220;
        if (near) { e.x += Math.sign(p.x - e.x) * e.speed * dt; e.baseY += Math.sign(p.y - 10 - e.baseY) * 45 * dt; }
        else { e.x += Math.sign(e.vx) * e.speed * 0.6 * dt; if (Math.abs(e.x - e.homeX) > 150) e.vx = -Math.sign(e.x - e.homeX); }
        e.y = e.baseY + Math.sin(this.t * 4 + e.seed) * 22;
      } else if (e.type === "ghost") {
        const a = Math.atan2(p.y - e.y, p.x - e.x);
        e.x += Math.cos(a) * e.speed * dt; e.y += Math.sin(a) * e.speed * dt;
      } else if (e.type === "turret") {
        this.physics(e, dt);
        e.fireT -= dt;
        if (e.fireT <= 0 && Math.abs(p.x - e.x) < 520) {
          const a = Math.atan2(p.y - e.y, p.x - e.x), s = (150 + L * 10) * Math.min(1.2, this.d.enemySpeed);
          this.bullets.push({ x: e.x, y: e.y - 6, vx: Math.cos(a) * s, vy: Math.sin(a) * s, r: 5 });
          e.fireT = rnd(2, 3) / this.d.bullets / (1 + L * 0.04);
          sfx("shoot");
        }
      } else {
        if (e.kbT > 0) e.kbT -= dt;
        else if (e.onGround) {
          const dir = Math.sign(e.vx) || 1;
          const aheadX = Math.floor((e.x + dir * (e.w + 2)) / TILE), footY = Math.floor((e.y + e.h + 4) / TILE);
          const charge = e.type === "beetle" && Math.abs(p.x - e.x) < 260 && Math.abs(p.y - e.y) < 50;
          e.charging = charge;
          if (e.hitWall || !this.solid(aheadX, footY, true)) e.vx = -dir * e.speed;
          else e.vx = (charge ? Math.sign(p.x - e.x) * 2.3 : dir) * e.speed;
          if (e.type === "jam") {
            e.jumpT -= dt;
            if (e.jumpT <= 0) { e.vy = -520; e.vx = Math.sign(p.x - e.x) * e.speed * 1.4; e.jumpT = rnd(1.2, 2.2); }
          }
        }
        this.physics(e, dt);
        if (e.y > H + 80) { this.enemies = this.enemies.filter((x) => x !== e); continue; }
      }
      const dx = Math.abs(e.x - p.x), dy = e.y - p.y;
      if (dx < e.r + p.w && Math.abs(dy) < e.r + p.h) {
        if (this.pw.star > 0) this.damageEnemy(e, 99);
        else if (p.vy > 50 && dy > e.r * 0.4) { p.vy = -480; this.damageEnemy(e, 2); if (++this.run.stomps >= 10) this.achieve("stomp_10"); }
        else this.hurtPlayer(e.x);
      }
    }
  }

  spawnBoss() {
    const c = this.bossCfg;
    const pw = bossPower(this.diffKey, !!c.overlord);
    const hp = Math.round(c.hp * this.d.enemyHp * pw.hp);
    const r = c.overlord ? 32 : ENEMIES[c.type].r * c.scale;
    this.boss = { ...c, isBoss: true, x: this.arenaX + 22 * TILE, y: c.fly ? 6 * TILE : 12 * TILE, r, w: r, h: r, hp, maxHp: hp, vx: -c.speed, vy: 0, hitT: 0, ringT: 2.5, aimT: 2, jumpT: 2, enraged: false, seed: 1, swoopT: 4, diveT: 0, kit: kitFor(c.name), pw, move: null, moveT: 3, moveIdx: 0, face: -1 };
    this.bossActive = true;
    this.phase = "bossintro"; this.phaseT = 3.2;
    this.cam = Math.max(this.arenaX, Math.min(this.levelW - W, this.cam));
    this.input.atk = false;
    sfx("boss");
    playMusic("boss", this.run.level);
  }

  groundTop(px) {
    const tx = Math.floor(px / TILE);
    if (tx < 1 || tx >= this.cols - 1) return -1;
    for (let ty = 0; ty < ROWS; ty++) if (this.grid[ty][tx] === 1) return ty;
    return -1;
  }

  startBossMove(b) {
    const kind = b.kit.moves[b.moveIdx++ % b.kit.moves.length];
    b.move = { kind, t: 0, stage: 0 };
    this.text(b.x, b.y - b.r - 26, moveLabel(kind, b.kit), "#FDE047");
    const p = this.p, lo = this.arenaX + TILE, hi = this.levelW - 2 * TILE, easy = this.diffKey === "easy";
    if (kind === "rain") {
      const n = Math.round((b.enraged ? 7 : 5) * (easy ? 0.6 : 1));
      for (let i = 0; i < n; i++) this.hazards.push({ type: "fall", look: b.kit.rain, r: 10, t: 0, warn: 0.8 + i * 0.18, vx: 0, vy: 0, rot: 0, x: Math.max(lo, Math.min(hi, p.x + (i ? rnd(-220, 220) : 0))), y: 22 });
    } else if (kind === "pillars") {
      const n = b.enraged ? 4 : 3;
      for (let i = 0; i < n; i++) {
        const x = Math.max(lo, Math.min(hi, p.x + (i - (n - 1) / 2) * 85)), top = this.groundTop(x);
        if (top > 0) this.hazards.push({ type: "burst", look: b.kit.pillar, r: 20, t: 0, warn: 0.9 + i * 0.25, x: Math.floor(x / TILE) * TILE + 16, y: top * TILE });
      }
    }
  }

  runBossMove(b, dt, sp) {
    const m = b.move, p = this.p, toward = Math.sign(p.x - b.x) || 1;
    m.t += dt;
    const end = () => { b.move = null; b.moveT = moveCooldown(b, this.diffKey); };
    const next = (st) => { m.stage = st; m.t = 0; };
    if (m.kind === "dash") {
      if (m.stage === 0) { b.vx = 0; m.dir = toward; b.face = toward; if (b.fly) b.y += (p.y - b.y) * dt * 3; if (m.t > 0.6) next(1); }
      else {
        b.vx = m.dir * sp * 3.4;
        if (b.fly) b.x += b.vx * dt;
        const wall = b.x <= this.arenaX + b.r + 2 || b.x >= this.levelW - TILE - b.r - 2;
        if (m.t > 0.9 || (m.t > 0.1 && wall)) end();
      }
      if (!b.fly) this.physics(b, dt);
    } else if (m.kind === "slam") {
      if (m.stage === 0) { b.vx = 0; if (m.t > 0.35 && b.onGround) { b.vy = -780; b.vx = toward * sp * 1.3; next(1); } }
      this.physics(b, dt);
      if (m.stage === 1 && m.t > 0.3 && b.onGround) {
        const gy = b.y + b.h - 9;
        [-1, 1].forEach((d) => this.bullets.push({ x: b.x + d * b.r, y: gy, vx: d * 270, vy: 0, r: 9, wave: b.kit.wave }));
        this.burst(b.x, b.y + b.h, b.kit.wave, 24, 200);
        sfx("boss");
        end();
      }
      if (m.t > 3) end();
    } else if (m.kind === "swing") {
      const reach = 70 + b.r * 0.5;
      if (m.stage === 0) {
        if (b.fly) { b.x += toward * sp * 1.6 * dt; b.y += (p.y - 6 - b.y) * dt * 3; }
        else { b.vx = toward * sp * 1.6; this.physics(b, dt); }
        if (Math.abs(p.x - b.x) < reach || m.t > 1.6) next(1);
        return;
      }
      if (!b.fly) { b.vx = 0; this.physics(b, dt); }
      if (m.stage === 1 && m.t > 0.45) { next(2); sfx("swing"); }
      else if (m.stage === 2) {
        if (!m.hit && Math.sign(p.x - b.x) === b.face && Math.abs(p.x - b.x) < reach + p.w + 20 && Math.abs(p.y - b.y) < b.r + 50) { m.hit = true; this.hurtPlayer(b.x); }
        if (m.t > 0.25) next(3);
      } else if (m.stage === 3 && m.t > 0.3) end();
    } else {
      if (b.fly) b.y += (5 * TILE - b.y) * dt * 2;
      else { b.vx = 0; this.physics(b, dt); }
      if (m.t > 1.3) end();
    }
  }

  updateBoss(dt) {
    const b = this.boss, p = this.p;
    if (!b) return;
    b.hitT -= dt;
    b.enraged = b.hp < b.maxHp / 2;
    const sp = b.speed * this.d.enemySpeed * (b.enraged ? 1.4 : 1) * b.pw.speed;
    const toward = Math.sign(p.x - b.x) || 1;
    if (!(b.move?.kind === "dash" && b.move.stage === 1)) b.face = toward;
    if (b.move) this.runBossMove(b, dt, sp);
    else {
      b.moveT -= dt;
      if (b.moveT <= 0 && !(b.diveT > 0)) this.startBossMove(b);
      if (b.fly) {
        if (b.diveT > 0) { b.diveT -= dt; if (b.diveT <= 0) b.swoopT = b.enraged ? 2.6 : 3.4; }
        else { b.swoopT -= dt; if (b.swoopT <= 0) { b.diveT = 3.6; this.text(b.x, b.y - 50, "SWOOPING DOWN!", "#FDE047"); } }
        const ty = b.diveT > 0 ? 16 * TILE - b.r - 6 : 5 * TILE + Math.sin(this.t * 1.5) * 60;
        b.x += toward * sp * dt * (b.diveT > 0 ? 0.35 : 0.8);
        b.y += (ty - b.y) * dt * 3;
      } else {
        if (b.onGround) {
          b.vx = toward * sp;
          b.jumpT -= dt;
          if (b.jump && b.jumpT <= 0) { b.vy = -620; b.jumpT = rnd(1.6, 2.6) / (b.enraged ? 1.5 : 1); }
        }
        this.physics(b, dt);
      }
      b.ringT -= dt; b.aimT -= dt;
      if (b.ring && b.ringT <= 0 && !(b.diveT > 0)) {
        const n = Math.max(4, Math.round(b.ring * this.d.bullets * (b.enraged ? 1.3 : 1)));
        for (let i = 0; i < n; i++) {
          const ang = Math.PI + (i / (n - 1)) * Math.PI;
          this.bullets.push({ x: b.x, y: b.y, vx: Math.cos(ang) * 150, vy: Math.sin(ang) * 150, r: 6 });
        }
        b.ringT = (b.enraged ? 1.6 : 2.4) / Math.sqrt(this.d.bullets) / b.pw.atk;
        sfx("shoot");
      }
      if (b.aim && b.aimT <= 0) {
        const a = Math.atan2(p.y - b.y, p.x - b.x);
        for (let i = 0; i < b.aim; i++) {
          const ang = a + (i - (b.aim - 1) / 2) * 0.22;
          this.bullets.push({ x: b.x, y: b.y, vx: Math.cos(ang) * 230, vy: Math.sin(ang) * 230, r: 5 });
        }
        b.aimT = (b.enraged ? 0.9 : 1.5) / this.d.bullets / b.pw.atk;
      }
    }
    b.x = Math.max(this.arenaX + b.r, Math.min(this.levelW - TILE - b.r, b.x));
    const dx = Math.abs(b.x - p.x), dy = b.y - p.y;
    if (dx < b.r + p.w && Math.abs(dy) < b.r + p.h) {
      if (p.vy > 50 && dy > b.r * 0.5) { p.vy = -600; this.damageEnemy(b, 2); }
      else this.hurtPlayer(b.x);
    }
  }

  drawBossWeapon(b) {
    const ctx = this.ctx, w = WEAPONS[b.kit.weapon], f = b.face || -1, base = f > 0 ? 0 : Math.PI, m = b.move;
    const by = b.y + (b.overlord ? Math.sin(this.t * 3) * 4 : 0), k = b.overlord ? 1.6 : 1.9;
    const hx = b.x + f * b.r * 0.75, hy = by + b.r * 0.1, a = weaponAngle(b, base, f);
    if (m?.kind === "swing" && m.stage === 2) {
      const reach = 70 + b.r * 0.5;
      ctx.fillStyle = "rgba(255,255,255,0.28)"; ctx.beginPath(); ctx.moveTo(hx, hy);
      ctx.arc(hx, hy, reach, Math.min(base - f * 2.2, a), Math.max(base - f * 2.2, a)); ctx.closePath(); ctx.fill();
    }
    ctx.save(); ctx.translate(hx, hy); ctx.scale(k, k);
    drawWeapon(ctx, 0, 0, a, w, Math.max(14, w.range - 22));
    ctx.restore();
    if (isWindup(b) && Math.sin(this.t * 24) > -0.3) {
      ctx.font = '26px "Lilita One", sans-serif'; ctx.textAlign = "center"; ctx.lineWidth = 4; ctx.strokeStyle = "#4A2511";
      ctx.strokeText("!", b.x, by - b.r - 8); ctx.fillStyle = "#EF4444"; ctx.fillText("!", b.x, by - b.r - 8);
    }
  }

  spawnHazard() {
    const hz = pickHazard(this.hazard), p = this.p;
    const topAt = (px) => this.groundTop(px);
    const h = { type: hz.type, look: hz.look, r: hz.r, w: hz.r, h: hz.r, t: 0, warn: 0, vx: 0, vy: 0, rot: 0, hops: 0 };
    if (hz.type === "roll") {
      h.x = Math.min(this.levelW - 3 * TILE, this.cam + W - 30);
      const top = topAt(h.x);
      if (top < 0 || h.x < p.x + 120) return;
      h.y = top * TILE - h.r - 1; h.warn = 0.6; h.vx = -rnd(150, 200) * Math.sqrt(this.d.enemySpeed);
    } else if (hz.type === "fall") {
      h.x = Math.max(TILE * 2, Math.min(this.levelW - TILE * 2, p.x + p.vx * 0.7 + rnd(-50, 50)));
      h.y = 22; h.warn = 0.9;
      if (hz.look === "meteor") { h.x += 140; h.vx = -170; h.vy = 260; h.y = 10; h.warn = 0.7; }
    } else {
      const bx = p.x + p.vx * 0.5 + rnd(-30, 140), top = topAt(bx);
      if (top < 0) return;
      h.x = Math.floor(bx / TILE) * TILE + 16; h.y = top * TILE; h.warn = 1.1;
    }
    this.hazards.push(h);
    this.hazardsSpawned++;
  }

  hazardHurt(x) { this.hazardHit = true; this.hurtPlayer(x); }

  popHazard(h) { this.burst(h.x, h.y, hazardColor(h.look), 12, 140); fx.ring(this.fx, h.x, h.y, hazardColor(h.look), 30); return false; }

  updateHazards(dt) {
    const p = this.p;
    this.hazardT -= dt;
    if (this.hazard && this.hazardT <= 0) { this.spawnHazard(); this.hazardT = hazardInterval(this.run.level, this.d) * (this.bossActive && !this.bossDead ? 1.6 : 1); }
    this.hazards = this.hazards.filter((h) => {
      h.t += dt;
      if (h.dead) return false;
      if (h.t < h.warn) return true;
      if (h.type === "burst") {
        const live = h.t - h.warn;
        if (live > 0.8) return false;
        const [dk, lt] = burstColors(h.look), top = h.y - 120 * Math.min(1, live / 0.15);
        if (Math.random() < 0.5) this.parts.push({ x: h.x + rnd(-12, 12), y: h.y - rnd(0, 100), vx: rnd(-40, 40), vy: -rnd(80, 200), life: 0.5, color: Math.random() < 0.5 ? dk : lt, size: ri(2, 4) });
        if (Math.abs(p.x - h.x) < 15 + p.w && p.y + p.h > top && p.y - p.h < h.y) this.hazardHurt(h.x);
        return true;
      }
      if (h.type === "roll") {
        this.physics(h, dt);
        if (h.hitWall) { h.vy = -560; if (++h.hops > 4) return this.popHazard(h); }
        h.rot += (h.vx * dt) / h.r;
        if (h.y > H + 40 || h.x < this.cam - 80) return false;
      } else {
        h.vy += (h.look === "meteor" ? 500 : 1500) * dt; h.x += h.vx * dt; h.y += h.vy * dt;
        if (h.y > H + 40 || this.solid(Math.floor(h.x / TILE), Math.floor((h.y + h.r) / TILE), true)) return this.popHazard(h);
      }
      if (Math.abs(h.x - p.x) < h.r + p.w && Math.abs(h.y - p.y) < h.r + p.h) { this.hazardHurt(h.x); return this.popHazard(h); }
      return true;
    });
  }

  updateBullets(dt) {
    const p = this.p;
    this.bullets = this.bullets.filter((q) => {
      q.x += q.vx * dt; q.y += q.vy * dt;
      if (q.y < -40 || q.y > H + 40 || (q.wave && q.x < this.arenaX) || this.solid(Math.floor(q.x / TILE), Math.floor(q.y / TILE))) return false;
      if (Math.abs(q.x - p.x) < q.r + p.w && Math.abs(q.y - p.y) < q.r + p.h) { this.hurtPlayer(q.x); return false; }
      return true;
    });
  }

  updateItems(dt) {
    const p = this.p;
    this.toasts = this.toasts.filter((q) => {
      const dist = Math.hypot(q.x - p.x, q.y - p.y);
      if (this.pw.magnet > 0 && dist < 240) { q.x += ((p.x - q.x) / dist) * 340 * dt; q.y += ((p.y - q.y) / dist) * 340 * dt; }
      if (dist > p.r + 14) return true;
      const n = (this.pw.double > 0 ? 2 : 1) * (q.secret ? 2 : 1);
      if (q.secret) { this.addScore(400); this.achieve("secret_toast"); }
      this.levelToast += n; this.run.toastTotal += n;
      this.addScore(100 * n);
      this.text(q.x, q.y - 14, `+${n} TOAST`);
      this.burst(q.x, q.y, "#FDE68A", 10);
      fx.ring(this.fx, q.x, q.y, "#FFD43B", 26); fx.glint(this.fx, q.x, q.y);
      sfx("toast");
      this.achieve("first_toast");
      if (this.run.toastTotal >= 100) this.achieve("toast_hoarder");
      return false;
    });
    this.pickups = this.pickups.filter((q) => {
      if (Math.hypot(q.x - p.x, q.y - p.y) > p.r + 16) return true;
      if (q.type === "power") this.applyPower(q.kind);
      else if (q.type === "heart") {
        if (p.hp >= this.maxHp) return true;
        p.hp = Math.min(this.maxHp, p.hp + 2);
        this.text(q.x, q.y - 14, "+HEART", "#FCA5A5");
        sfx("heart");
      } else {
        this.run.weapon = q.weapon;
        this.text(q.x, q.y - 20, WEAPONS[q.weapon].name.toUpperCase() + "!", "#FDE047");
        this.burst(q.x, q.y, "#FDE047", 30, 180);
        sfx("weapon");
        this.achieve({ dinner_fork: "fork_found", baguette_sword: "baguette", golden_spatula: "spatula" }[q.weapon]);
      }
      return false;
    });
    if (!this.exitOpen && this.levelToast >= this.goal && this.bossDead) {
      this.exitOpen = true;
      this.text(p.x, p.y - 30, "EXIT OPEN! HEAD RIGHT >>", "#FDE047");
      sfx("level");
    }
    if (this.exitOpen && Math.abs(this.exit.x - p.x) < 24 && Math.abs(this.exit.y - p.y) < 40) this.levelComplete();
  }

  // ---------- draw ----------
  draw() {
    const ctx = this.ctx, t = this.t, p = this.p, cam = Math.round(this.cam);
    ctx.imageSmoothingEnabled = true;
    if (!this.skyGrad) {
      this.skyGrad = ctx.createLinearGradient(0, 0, 0, H);
      this.skyGrad.addColorStop(0, this.pal.skyTop); this.skyGrad.addColorStop(1, this.pal.skyBot);
    }
    ctx.fillStyle = this.skyGrad; ctx.fillRect(0, 0, W, H);
    drawBiome(ctx, this.pal, cam, this.t, W, H);
    ctx.save();
    ctx.translate(-cam, 0);
    ctx.drawImage(this.bg, 0, 0);
    if (this.arenaX && !this.bossDead) { ctx.fillStyle = "rgba(239,68,68,0.35)"; ctx.fillRect(this.arenaX, 0, 4, H); }
    this.drawExit();
    ctx.fillStyle = "rgba(255,212,59,0.18)";
    this.toasts.forEach((q) => { ctx.beginPath(); ctx.arc(q.x, q.y + Math.sin(t * 4 + q.seed) * 3, 17 + Math.sin(t * 5 + q.seed) * 2, 0, Math.PI * 2); ctx.fill(); });
    this.toasts.forEach((q) => drawToast(ctx, q.x, q.y + Math.sin(t * 4 + q.seed) * 3, q.secret ? 26 : 22, q.secret));
    this.pickups.forEach((q) => {
      const bob = Math.sin(t * 4) * 3;
      if (q.type === "heart") drawHeart(ctx, q.x, q.y + bob, 22);
      else if (q.type === "power") {
        const info = POWER_INFO[q.kind];
        ctx.fillStyle = info.color; ctx.strokeStyle = "#4A2511"; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(q.x, q.y + bob, 14, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
        ctx.fillStyle = "#FFFBEB"; ctx.beginPath(); ctx.arc(q.x, q.y + bob, 10, 0, Math.PI * 2); ctx.fill();
        ctx.font = '12px "Lilita One", sans-serif'; ctx.textAlign = "center";
        ctx.fillStyle = "#4A2511"; ctx.fillText(info.icon, q.x, q.y + 4 + bob);
      } else {
        ctx.fillStyle = "rgba(253,224,71,0.3)"; ctx.beginPath(); ctx.arc(q.x, q.y, 22, 0, Math.PI * 2); ctx.fill();
        drawWeapon(ctx, q.x - 26, q.y + bob, -0.6, WEAPONS[q.weapon], 22);
      }
    });
    const ang = (e) => Math.atan2(p.y - e.y, p.x - e.x);
    this.enemies.forEach((e) => { if (Math.abs(e.x - p.x) < W) drawEnemy(ctx, e, t, ang(e)); });
    this.hazards.forEach((h) => drawHazard(ctx, h, t));
    const b = this.boss;
    if (b && !this.bossDead) {
      if (b.overlord) drawBoss(ctx, b, t, ang(b));
      else {
        ctx.save(); ctx.translate(b.x, b.y); ctx.scale(b.scale, b.scale);
        drawBossExtras(ctx, b.name, ENEMIES[b.type].r, t, "back");
        drawEnemy(ctx, { ...b, x: 0, y: 0, r: ENEMIES[b.type].r }, t, ang(b));
        drawBossExtras(ctx, b.name, ENEMIES[b.type].r, t, "front");
        ctx.restore();
      }
      this.drawBossWeapon(b);
    }
    if (!["gameover", "dying", "done"].includes(this.phase) || this.phaseT > 1.6) {
      if (this.pw.star > 0) { ctx.fillStyle = "rgba(253,224,71,0.35)"; ctx.beginPath(); ctx.arc(p.x, p.y, 22, 0, Math.PI * 2); ctx.fill(); }
      if (this.pw.shield) { ctx.strokeStyle = "#67E8F9"; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(p.x, p.y, 21, 0, Math.PI * 2); ctx.stroke(); }
      ctx.globalAlpha = p.inv > 0 ? 0.6 : 1;
      drawHero(ctx, { ...p, y: p.y - 4 }, t, this.phase === "exiting" || this.phase === "victory", this.skin);
      ctx.globalAlpha = 1;
      this.drawPlayerWeapon();
    }
    this.bullets.forEach((q) => {
      if (q.wave) {
        ctx.fillStyle = q.wave; ctx.strokeStyle = "#4A2511"; ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.ellipse(q.x, q.y + q.r, q.r * 1.6, q.r * 2, 0, Math.PI, 0); ctx.closePath(); ctx.fill(); ctx.stroke();
        ctx.fillStyle = "rgba(255,255,255,0.45)"; ctx.beginPath(); ctx.ellipse(q.x - q.r * 0.4, q.y - q.r * 0.3, q.r * 0.4, q.r * 0.25, 0, 0, Math.PI * 2); ctx.fill();
        return;
      }
      ctx.fillStyle = "rgba(249,115,22,0.35)"; ctx.beginPath(); ctx.arc(q.x, q.y, q.r * 1.9, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#F97316"; ctx.beginPath(); ctx.arc(q.x, q.y, q.r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#FEF08A"; ctx.beginPath(); ctx.arc(q.x - 1, q.y - 1, q.r * 0.45, 0, Math.PI * 2); ctx.fill();
    });
    this.parts.forEach((q) => { ctx.globalAlpha = Math.min(1, q.life * 2); ctx.fillStyle = q.color; ctx.beginPath(); ctx.arc(q.x, q.y, q.size, 0, Math.PI * 2); ctx.fill(); });
    drawFx(ctx, this.fx);
    ctx.globalAlpha = 1;
    ctx.font = '15px "Lilita One", sans-serif'; ctx.textAlign = "center";
    this.texts.forEach((q) => {
      ctx.globalAlpha = Math.min(1, q.life * 2);
      ctx.lineWidth = 3; ctx.strokeStyle = "#4A2511"; ctx.strokeText(q.txt, q.x, q.y);
      ctx.fillStyle = q.color; ctx.fillText(q.txt, q.x, q.y);
    });
    ctx.globalAlpha = 1;
    ctx.restore();
    drawVignette(ctx, W, H);
    drawFade(ctx, W, H, this.phase === "intro" ? (this.phaseT - 1.9) / 0.5 : this.phase === "exiting" ? (0.5 - this.phaseT) / 0.5 : 0);
    this.drawBanner();
  }

  drawBossIntro() {
    const ctx = this.ctx, k = Math.min(1, (3.2 - this.phaseT) / 0.5), c = this.bossCfg;
    ctx.fillStyle = "rgba(0,0,0,0.55)"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#0B0F1A"; ctx.fillRect(0, 0, W, 90 * k); ctx.fillRect(0, H - 90 * k, W, 90 * k);
    ctx.fillStyle = "#7F1D1D"; ctx.fillRect(W - W * k, H / 2 - 80, W, 120);
    ctx.fillStyle = "#EF4444"; ctx.fillRect(W - W * k, H / 2 - 80, W, 4); ctx.fillRect(W - W * k, H / 2 + 36, W, 4);
    ctx.textAlign = "center";
    ctx.font = '14px "Lilita One", sans-serif'; ctx.fillStyle = "#FCA5A5";
    ctx.fillText(`STAGE ${this.run.level} BOSS`, W / 2, H / 2 - 50);
    ctx.font = '46px "Lilita One", sans-serif';
    ctx.fillStyle = "#000"; ctx.fillText(c.name.toUpperCase(), W / 2 + 4, H / 2 + 8);
    ctx.fillStyle = "#FDE047"; ctx.fillText(c.name.toUpperCase(), W / 2, H / 2 + 4);
    const chars = Math.floor(Math.max(0, 3.2 - this.phaseT - 0.6) * 40);
    ctx.font = '15px "Lilita One", sans-serif'; ctx.fillStyle = "#F8FAFC";
    ctx.fillText(`"${c.taunt.slice(0, chars)}"`, W / 2, H / 2 + 90);
  }

  drawExit() {
    const ctx = this.ctx, ex = this.exit, open = this.exitOpen, t = this.t;
    if (open) { ctx.fillStyle = `rgba(255,212,59,${0.25 + Math.sin(t * 3) * 0.08})`; ctx.beginPath(); ctx.arc(ex.x, ex.y - 6, 46, 0, Math.PI * 2); ctx.fill(); }
    ctx.fillStyle = "rgba(74,37,17,0.25)"; ctx.beginPath(); ctx.ellipse(ex.x, ex.y + 22, 30, 6, 0, 0, Math.PI * 2); ctx.fill();
    ctx.lineWidth = 3; ctx.strokeStyle = "#4A2511";
    ctx.fillStyle = open ? "#F8FAFC" : "#CBD5E1"; ctx.beginPath(); ctx.ellipse(ex.x, ex.y + 16, 30, 8, 0, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = open ? "#FFD43B" : "#94A3B8"; ctx.beginPath(); ctx.roundRect(ex.x - 18, ex.y - 8, 36, 22, 5); ctx.fill(); ctx.stroke();
    ctx.fillStyle = open ? "#FFF0A0" : "#E2E8F0"; ctx.beginPath(); ctx.roundRect(ex.x - 14, ex.y - 5, 28, 6, 3); ctx.fill();
    ctx.font = '16px "Lilita One", sans-serif'; ctx.textAlign = "center"; ctx.lineWidth = 4; ctx.strokeStyle = "#4A2511";
    const label = open ? "GOAL!" : "LOCKED";
    ctx.strokeText(label, ex.x, ex.y - 22); ctx.fillStyle = open ? "#FFD43B" : "#E2E8F0"; ctx.fillText(label, ex.x, ex.y - 22);
  }

  drawPlayerWeapon() {
    const p = this.p, w = WEAPONS[this.run.weapon], ctx = this.ctx;
    const len = Math.max(14, w.range - 22);
    const base = p.dir > 0 ? 0 : Math.PI;
    if (p.swing > 0) {
      const k = 1 - p.swing / 0.16;
      const a = base + p.dir * (-w.arc / 2 + w.arc * k);
      ctx.fillStyle = "rgba(254,240,138,0.18)";
      ctx.beginPath(); ctx.moveTo(p.x, p.y);
      ctx.arc(p.x, p.y, w.range + 6, Math.min(base - w.arc / 2, a), Math.max(base - w.arc / 2, a)); ctx.closePath(); ctx.fill();
      drawWeapon(ctx, p.x, p.y, a, w, len);
    } else drawWeapon(ctx, p.x, p.y, base - p.dir * 0.9, w, len * 0.6);
  }

  drawBanner() {
    const ctx = this.ctx, L = this.run.level;
    const map = {
      intro: [`LEVEL ${L}`, `${this.theme.name.toUpperCase()} — COLLECT ${this.goal} TOAST${this.bossCfg ? ` & BEAT THE ${this.bossCfg.name.toUpperCase()}` : ""}`],
      dying: [this.timedOut ? "TIME UP!" : "OUCH! LIFE LOST", `${this.run.lives} ${this.run.lives === 1 ? "LIFE" : "LIVES"} LEFT — RESTARTING LEVEL`],
      gameover: ["BURNT TOAST", "ALL LIVES LOST"],
      exiting: ["LEVEL CLEAR!", "BUTTER BOY MARCHES ON..."],
      victory: ["YOU DID IT!", "THE TOAST IS YOURS"],
      dailydone: ["DAILY CLEARED!", "SUBMITTING YOUR SCORE..."],
    };
    if (this.phase === "bossintro") return this.drawBossIntro();
    const b = map[this.phase];
    if (!b) return;
    ctx.fillStyle = "rgba(74,37,17,0.82)"; ctx.beginPath(); ctx.roundRect(W / 2 - 400, H / 2 - 75, 800, 145, 28); ctx.fill();
    ctx.strokeStyle = "#FFD43B"; ctx.lineWidth = 5; ctx.stroke();
    ctx.textAlign = "center";
    ctx.font = '58px "Lilita One", sans-serif';
    ctx.fillStyle = "#4A2511"; ctx.fillText(b[0], W / 2 + 3, H / 2 + 4);
    ctx.fillStyle = this.phase === "gameover" || this.phase === "dying" ? "#EF4444" : "#FDE047";
    ctx.fillText(b[0], W / 2, H / 2);
    ctx.font = '19px "Lilita One", sans-serif';
    ctx.fillStyle = "#FFF7E6"; ctx.fillText(b[1], W / 2, H / 2 + 42);
    if (this.phase === "intro") {
      ctx.font = '14px "Lilita One", sans-serif'; ctx.fillStyle = "#FFD43B"; ctx.fillText(`Now playing: ${songName(L)}`, W / 2, H / 2 + 64);
      if (this.hazard) { ctx.fillStyle = "#FCA5A5"; ctx.fillText(`WATCH OUT: ${this.hazard.label}!`, W / 2, H / 2 - 52); }
    }
  }
}
